import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api/client';
import type { Branch } from '../api/types';

/**
 * Which shop the customer is ordering from.
 *
 * Remembered in localStorage, because somebody who collects from Randburg Square
 * every morning should not have to say so every morning. The chosen shop is
 * re-checked against the live list on load: a branch that has since closed
 * silently falls back to the default rather than failing at checkout, which is
 * the worst possible moment to discover it.
 *
 * Only one branch is trading today, so the picker hides itself. It appears the
 * moment a second one opens, with no deploy.
 */

const STORAGE_KEY = 'esmoothies.branch';

interface BranchState {
  branches: Branch[];
  selected: Branch | null;
  /** False while the list is still loading. */
  ready: boolean;
  /** Nothing to choose between — hide the picker entirely. */
  onlyOne: boolean;
  choose: (id: string) => void;
}

const BranchCtx = createContext<BranchState | null>(null);

export function BranchProvider({ children }: { children: ReactNode }) {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const list = await api.branches();
        if (cancelled) return;

        setBranches(list);

        const remembered = localStorage.getItem(STORAGE_KEY);
        const stillOpen = list.find((b) => b.id === remembered);

        setSelectedId(stillOpen?.id ?? list.find((b) => b.isDefault)?.id ?? list[0]?.id ?? null);
      } catch {
        // A branch list we cannot fetch must not stop somebody ordering — the
        // API falls back to the default branch when none is sent.
        if (!cancelled) setBranches([]);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const choose = useCallback((id: string) => {
    setSelectedId(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const value = useMemo<BranchState>(
    () => ({
      branches,
      selected: branches.find((b) => b.id === selectedId) ?? null,
      ready,
      onlyOne: branches.length <= 1,
      choose,
    }),
    [branches, selectedId, ready, choose],
  );

  return <BranchCtx.Provider value={value}>{children}</BranchCtx.Provider>;
}

export function useBranch() {
  const ctx = useContext(BranchCtx);
  if (!ctx) throw new Error('useBranch must be used inside a BranchProvider');
  return ctx;
}
